/**
 * The registered-user total behind every marketing figure: the homepage's
 * "loved by a community of more than …" line, and on landing v3 the trusted
 * pill and the "+2M students study this way" headline.
 *
 * The number comes from `/api/user-count` (server/routes/user-count.ts), which
 * is the only side that can reach the auth worker. The fetch is one GET on
 * mount against a CDN-cached route, so a signed-out visitor costs nothing
 * beyond an edge hit.
 *
 * Every failure — the route 503ing, a transport error, an unparseable body —
 * resolves to `null`, and callers render {@link FALLBACK_USER_COUNT} instead.
 * The copy is the same shape either way, so the line never reflows when the
 * live figure lands.
 */

import { useEffect, useState } from 'react';

// The pure formatters live apart so the edge middleware (which renders the
// same figures into crawler HTML) can import them without React.
export {
  FALLBACK_USER_COUNT,
  formatCompactMillions,
  formatExactCount,
} from '@/lib/user-count-format';

/** The live count, or `null` while it is unknown. */
export function useLiveUserCount(): number | null {
  const [users, setUsers] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/user-count', { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { users?: unknown } | null) => {
        const value = body?.users;
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
          setUsers(Math.floor(value));
        }
      })
      // Includes the abort on unmount. Nothing to report: the fallback is the
      // answer for every failure.
      .catch(() => {});
    return () => controller.abort();
  }, []);

  return users;
}
